import React, { useState, useEffect, useMemo } from "react";
import { Box, Heading, Text, Button, TextInput, Layer, Spinner, CheckBoxGroup, DropButton } from "grommet";
import { Search, Add, FormDown, FormCheckmark } from "grommet-icons";
import axios from "axios";
import { Notification } from "grommet";
import { HeaderNavigation } from "../../components/Headers/PursuitHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import AssessmentTable from "../../components/PursuitAccess/UserAssessmentsTable";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import countryRegion from "../../components/PursuitAccess/country_region.json";
import ExportAssessmentsButton from "../../components/PursuitAccess/ExportAssessmentsButton";

const dateOptions = [
    { label: "1 Week", value: "1w" },
    { label: "1 Month", value: "1m" },
    { label: "3 Months", value: "3m" },
    { label: "Max (6 months)", value: "all" },
];

const userTypeOptions = [
    { label: "Manager", value: "1" },
    { label: "Invited User", value: "2" },
    { label: "Self Registered User", value: "0" },
];

const regionOptions = Object.keys(countryRegion).map((region) => ({
    label: region,
    value: region,
}));

const UserAssessmentsPage = () => {
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [dateRange, setDateRange] = useState("all");
    const [userType, setUserType] = useState([]);
    const [regions, setRegions] = useState([]);
    const [error, setError] = useState(null);
    const [commFilter, setCommFilter] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);


    const selectedCountries = useMemo(() => {
        // no filter when nothing selected
        if (!Array.isArray(regions) || regions.length === 0) return null;

        // build single array of all countries in those regions
        const all = regions.reduce((acc, reg) => {
            const list = countryRegion[reg] || [];
            return acc.concat(list);
        }, []);

        // normalize to lower‐case for easy matching
        return new Set(all.map(c => c.toLowerCase()));
    }, [regions]);

    // Fetch assessments data from the backend
    useEffect(() => {
        const fetchAssessments = async () => {
            setLoading(true);
            try {
                const response = await axios.get("/api/pursuit/get-user-created-completed-assessments");
                setAssessments(response.data.assessments);
            } catch (err) {
                setError("Error fetching assessments");
            } finally {
                setLoading(false);
            }
        };

        fetchAssessments();
    }, []);

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value);
    };

    const handleUserTypeChange = (value) => {
        setUserType(value);
    };

    const filteredAssessments = assessments.filter((assessment) => {
        const lower = searchValue.toLowerCase().trim();

        // build searchable strings
        const name = (assessment.user_name || "").toLowerCase();
        const email = (assessment.user_email || "").toLowerCase();
        const topics = [
            assessment.topic1_name,
            assessment.topic2_name,
            assessment.topic3_name,
            assessment.topic4_name,
            assessment.topic5_name,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        const dateStr = new Date(assessment.end_date)
            .toLocaleDateString() // e.g. "7/1/2025"
            .toLowerCase();
        const user_country = (assessment.user_country || "").toLowerCase();

        // any‐field match
        const matchesSearch =
            lower === "" ||
            name.includes(lower) ||
            email.includes(lower) ||
            topics.includes(lower) ||
            dateStr.includes(lower) ||
            user_country.includes(lower);

        const matchesDateRange =
            dateRange === "all" ||
            (dateRange === "1w" && new Date(assessment.end_date) >= new Date(Date.now() - 7 * 24 * 3600e3)) ||
            (dateRange === "1m" && new Date(assessment.end_date) >= new Date(new Date().setMonth(new Date().getMonth() - 1))) ||
            (dateRange === "3m" && new Date(assessment.end_date) >= new Date(new Date().setMonth(new Date().getMonth() - 3)));

        const matchesUserType =
            userType.length === 0 ||
            userType.includes(String(assessment.user_type));

        const matchesRegion =
            !selectedCountries ||
            selectedCountries.has(
                (assessment.user_country || "").toLowerCase()
            );

        const matchesComm =
            commFilter.length === 0 ||
            commFilter.includes(String(assessment.user_agree_to_communicate));


        return (
            matchesSearch && matchesDateRange && matchesUserType && matchesRegion && matchesComm
        );


    });

    const usersToMark = filteredAssessments.filter(
        (a) => a.user_is_communicated === 0
    );

    const handleMarkAllCommunicated = async () => {
        setShowConfirm(false);
        setLoading(true);
        try {
            const uniqueUserIds = [
                ...new Set(usersToMark.map((u) => u.user_id))
            ];

            const payload = {
                user_ids: uniqueUserIds,
            };

            // Replace with actual API
            await axios.post("/api/pursuit/mark-all-users-as-contacted", payload);

            setToastMessage("Marked successfully. You will now see new users at the top on next login.");
            // Optionally re-fetch or mutate local state
        } catch (err) {
            setToastMessage("Something went wrong while marking users.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box fill="vertical" background="light-2">
            <HeaderNavigation />
            <BreadcrumbNavigation
                breadcrumbs={[
                    { label: "Dashboard", link: `/pursuit/dashboard` },
                    { label: "User Self Assessments", link: `/pursuit/self-assessments` },
                ]}
            />
            <Box height="2px" background="brand" margin={{ vertical: "small" }} />

            <Box flex pad={{ horizontal: "medium", vertical: "xsmall" }}>
                <Box direction="row" align="center" justify="between">
                    <Heading level="2" margin="none" color="brand">
                        User Self Assessments
                    </Heading>
                    <Box direction="row" gap="small" align="center">
                        <ExportAssessmentsButton
                            data={filteredAssessments}
                            dateRange={dateRange}
                        />
                        <Button
                            label="Go to Manager Created Assessments"
                            secondary
                            onClick={() => { window.location.href = "/pursuit/manager-assessments"; }}
                        />
                    </Box>
                </Box>
                <Text margin={{ bottom: "xsmall" }}> {/* tightened bottom margin */}
                    This page displays self-assessment data for users. You can filter the
                    data by date, search terms, and user types.
                </Text>

                {/* ——— Filters (fixed height, non-flex) ——— */}
                <Box
                    flex={false}
                    direction="row"
                    align="center"
                    gap="medium"
                    margin={{ top: "none", bottom: "small" }}
                >
                    {/* Search */}
                    <Box flex={false}>
                        <TextInput
                            icon={<Search />}
                            placeholder="Search assessments by user name, email, country or date"
                            value={searchValue}
                            onChange={handleSearchChange}
                            width="500px"
                        />
                    </Box>

                    {/* Date Range Dropdown */}
                    <DropButton
                        label={
                            dateOptions.find((opt) => opt.value === dateRange)?.label ||
                            "All Time"
                        }
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="small" gap="small">
                                {dateOptions.map((opt) => (
                                    <Button
                                        key={opt.value}
                                        hoverIndicator
                                        plain
                                        onClick={() => handleDateRangeChange(opt.value)}
                                    >
                                        <Box pad="xsmall">
                                            <Text
                                                color={dateRange === opt.value ? "brand" : undefined}
                                            >
                                                {opt.label}
                                            </Text>
                                        </Box>
                                    </Button>
                                ))}
                            </Box>
                        }
                    />

                    {/* User Type Dropdown */}
                    <DropButton
                        label="User Type"
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="small">
                                <CheckBoxGroup
                                    name="userType"
                                    options={userTypeOptions}
                                    value={userType}
                                    onChange={({ value }) => setUserType(value)}
                                />
                            </Box>
                        }
                    />
                    <DropButton
                        label="Region"
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="small">
                                <CheckBoxGroup
                                    options={regionOptions}
                                    value={regions}
                                    onChange={({ value }) => setRegions(value)}
                                />
                            </Box>
                        }
                    />
                    <DropButton
                        label="Communicate"
                        icon={<FormDown />}
                        dropAlign={{ top: "bottom", left: "left" }}
                        dropContent={
                            <Box pad="small">
                                <CheckBoxGroup
                                    name="commFilter"
                                    // two options: “Yes” (1) or “No” (0)
                                    options={[
                                        { label: "Has not agreed", value: "0" },
                                        { label: "Has agreed", value: "1" },
                                    ]}
                                    value={commFilter}
                                    onChange={({ value }) => setCommFilter(value)}
                                />
                            </Box>
                        }
                    />
                    <Box direction="row" justify="end" margin={{ top: "small", bottom: "small" }}>
                        <Button
                            // icon={<FormCheckmark />}
                            label="Mark All"
                            tip="Mark All as Contacted"
                            // primary
                            onClick={() => setShowConfirm(true)}
                            disabled={usersToMark.length === 0}
                        />
                    </Box>

                </Box>

                {/* ——— Table Area (flexes) ——— */}
                <Box flex overflow={{ horizontal: "auto" }} margin={{ top: "none" }}>
                    {loading ? (
                        <Box fill align="center" justify="center">
                            <Spinner size="large" />
                            <Text margin={{ top: "small" }}>Loading assessments…</Text>
                        </Box>
                    ) : error ? (
                        <Box fill align="center" justify="center">
                            <Text color="status-critical">{error}</Text>
                        </Box>
                    ) : (
                        <AssessmentTable
                            key={Array.isArray(regions) ? regions.join(",") : "no-regions"}
                            assessmentData={filteredAssessments}
                        />

                    )}
                </Box>
                {/* </Box> */}
            </Box>

            <StickyFooter />

            {showConfirm && (
                <Layer
                    onEsc={() => setShowConfirm(false)}
                    onClickOutside={() => setShowConfirm(false)}
                    responsive={false}
                    modal
                >
                    <Box pad="medium" gap="small" width="medium">
                        <Heading level="3" margin="none">
                            Confirm Action
                        </Heading>
                        <Text>
                            Are you sure you want to mark all uncontacted users as contacted?
                        </Text>
                        <Box direction="row" gap="medium" justify="end" margin={{ top: "small" }}>
                            <Button label="Cancel" onClick={() => setShowConfirm(false)} />
                            <Button label="Confirm" primary onClick={handleMarkAllCommunicated} />
                        </Box>
                    </Box>
                </Layer>
            )}

            {toastMessage && (
                <Notification
                    toast
                    status="normal"
                    title="Success"
                    message={toastMessage}
                    onClose={() => setToastMessage(null)}
                />
            )}



        </Box>
    );

};

export default UserAssessmentsPage;
